import React, {FC, useCallback, useEffect, useState} from "react";
import {
    Alert,
    App,
    Button,
    Card,
    Col,
    Empty,
    Form,
    Input,
    List,
    Modal,
    Row,
    Space,
    Spin,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import {CopyOutlined, LoginOutlined, PlusOutlined, TeamOutlined, UserOutlined} from "@ant-design/icons";
import {useGlobal} from "../config/GlobalContext";
import AxiosServices from "../Services/axiosServices";
import {ISqd} from "../Interfaces/ISqd";
import {IUsrSqdCmpEdt, IUsrSqdCmpEdtUsr} from "../Interfaces/IUsrSqdCmpEdt";

const {Title, Paragraph, Text} = Typography;

const ACCENT = '#203923';

const Squads: FC = () => {
    const {currentCmpEdt} = useGlobal();
    const {message} = App.useApp();

    const [squads, setSquads] = useState<ISqd[]>([]);
    const [loading, setLoading] = useState(false);

    const [createLib, setCreateLib] = useState("");
    const [creating, setCreating] = useState(false);
    const [createdSquad, setCreatedSquad] = useState<ISqd | null>(null);

    const [joinToken, setJoinToken] = useState("");
    const [joining, setJoining] = useState(false);

    const [selectedSquad, setSelectedSquad] = useState<ISqd | null>(null);
    const [members, setMembers] = useState<IUsrSqdCmpEdt[]>([]);
    const [membersLoading, setMembersLoading] = useState(false);

    const refreshSquads = useCallback(async () => {
        setLoading(true);
        try {
            const data = await AxiosServices.listSquads();
            setSquads(data);
        } catch (err: any) {
            message.error(err?.response?.data?.Erreur ?? err?.response?.data?.message ?? "Impossible de charger les squads.");
        } finally {
            setLoading(false);
        }
    }, [message]);

    useEffect(() => {
        setCreatedSquad(null);
        refreshSquads().then();
    }, [refreshSquads]);

    const handleCreate = async () => {
        const lib = createLib.trim();
        if (!lib) {
            message.warning("Donne un nom à la squad.");
            return;
        }
        if (!currentCmpEdt) {
            message.warning("Sélectionne d'abord une compétition.");
            return;
        }
        setCreating(true);
        try {
            const squad = await AxiosServices.createSquad(currentCmpEdt, lib);
            setCreatedSquad(squad);
            setCreateLib("");
            await refreshSquads();
            message.success(`Squad « ${squad.Lib} » créée.`);
        } catch (err: any) {
            message.error(err?.response?.data?.Erreur ?? err?.response?.data?.message ?? "Création impossible.");
        } finally {
            setCreating(false);
        }
    };

    const handleJoin = async () => {
        const token = joinToken.trim();
        if (!token) {
            message.warning("Colle un token d'invitation.");
            return;
        }
        setJoining(true);
        try {
            const joined = await AxiosServices.joinSquad(token);
            setJoinToken("");
            await refreshSquads();
            message.success(`Inscription à « ${joined.Sqd.Lib} » réussie.`);
        } catch (err: any) {
            message.error(err?.response?.data?.Erreur ?? err?.response?.data?.message ?? "Inscription impossible.");
        } finally {
            setJoining(false);
        }
    };

    const openSquadMembers = async (s: ISqd) => {
        setSelectedSquad(s);
        setMembers([]);
        setMembersLoading(true);
        try {
            const data = await AxiosServices.getSquadMembers(s.idt);
            setMembers(data);
        } catch (err: any) {
            message.error(err?.response?.data?.Erreur ?? err?.response?.data?.message ?? "Impossible de charger les membres.");
        } finally {
            setMembersLoading(false);
        }
    };

    const closeSquadMembers = () => {
        setSelectedSquad(null);
        setMembers([]);
    };

    const memberDisplayName = (u: IUsrSqdCmpEdtUsr): string => {
        if (u.username) return u.username;
        const full = [u.firstName, u.lastName].filter(Boolean).join(" ");
        return full || `Utilisateur #${u.idt}`;
    };

    const copyToken = async (token: string) => {
        try {
            await navigator.clipboard.writeText(token);
            message.success("Token copié.");
        } catch {
            message.error("Copie impossible.");
        }
    };

    return (
        <div style={{
            maxWidth: 980,
            margin: '0 auto',
            padding: '24px 24px 48px',
            color: '#1F2937',
            textAlign: 'left',
            lineHeight: 1.6,
        }}>
            <div style={{textAlign: 'center', marginBottom: 24}}>
                <Title level={2} style={{color: ACCENT, marginBottom: 4}}>
                    <TeamOutlined/> Squads
                </Title>
                <Paragraph type="secondary" style={{marginBottom: 0}}>
                    Crée ta squad ou rejoins-en une avec un token d'invitation.
                </Paragraph>
            </div>

            <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                    <Card
                        title={<><PlusOutlined/> Créer une squad</>}
                        style={{borderRadius: 12, height: '100%'}}
                    >
                        {!currentCmpEdt && (
                            <Alert
                                style={{marginBottom: 12}}
                                type="warning"
                                showIcon
                                message="Sélectionne d'abord une compétition pour créer une squad."
                            />
                        )}
                        <Form layout="vertical" onFinish={handleCreate}>
                            <Form.Item label="Nom de la squad" required>
                                <Input
                                    value={createLib}
                                    onChange={e => setCreateLib(e.target.value)}
                                    maxLength={100}
                                    placeholder="Ex : Les pros du penalty"
                                    disabled={creating || !currentCmpEdt}
                                />
                            </Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={creating}
                                disabled={!currentCmpEdt}
                                icon={<PlusOutlined/>}
                            >
                                Créer
                            </Button>
                        </Form>

                        {createdSquad && (
                            <Alert
                                style={{marginTop: 16}}
                                type="success"
                                showIcon
                                message={`Squad « ${createdSquad.Lib} » créée`}
                                description={
                                    <Space direction="vertical" style={{width: '100%'}} size={4}>
                                        <Text type="secondary">
                                            Partage ce token avec les membres pour qu'ils rejoignent :
                                        </Text>
                                        <Space.Compact style={{width: '100%'}}>
                                            <Input
                                                readOnly
                                                value={createdSquad.Cod}
                                                style={{fontFamily: 'monospace'}}
                                            />
                                            <Tooltip title="Copier">
                                                <Button
                                                    icon={<CopyOutlined/>}
                                                    onClick={() => copyToken(createdSquad.Cod)}
                                                />
                                            </Tooltip>
                                        </Space.Compact>
                                    </Space>
                                }
                            />
                        )}
                    </Card>
                </Col>

                <Col xs={24} md={12}>
                    <Card
                        title={<><LoginOutlined/> Rejoindre une squad</>}
                        style={{borderRadius: 12, height: '100%'}}
                    >
                        <Form layout="vertical" onFinish={handleJoin}>
                            <Form.Item label="Token d'invitation" required>
                                <Input
                                    value={joinToken}
                                    onChange={e => setJoinToken(e.target.value.toUpperCase())}
                                    placeholder="Colle le token reçu"
                                    style={{fontFamily: 'monospace'}}
                                    disabled={joining}
                                    maxLength={10}
                                />
                            </Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={joining}
                                icon={<LoginOutlined/>}
                            >
                                S'inscrire
                            </Button>
                        </Form>
                    </Card>
                </Col>
            </Row>

            <Card
                style={{marginTop: 16, borderRadius: 12}}
                title={<><TeamOutlined/> Toutes les squads</>}
            >
                <List
                    loading={loading}
                    locale={{emptyText: <Empty description="Aucune squad."/>}}
                    dataSource={squads}
                    renderItem={(s: ISqd) => (
                        <List.Item
                            actions={[
                                <Button
                                    key="members"
                                    type="link"
                                    icon={<UserOutlined/>}
                                    onClick={() => openSquadMembers(s)}
                                >
                                    Voir les membres
                                </Button>,
                            ]}
                        >
                            <List.Item.Meta
                                avatar={<TeamOutlined style={{fontSize: 20, color: ACCENT}}/>}
                                title={s.Lib}
                            />
                        </List.Item>
                    )}
                />
            </Card>

            <Modal
                open={!!selectedSquad}
                onCancel={closeSquadMembers}
                onOk={closeSquadMembers}
                cancelButtonProps={{style: {display: 'none'}}}
                okText="Fermer"
                title={
                    <Space>
                        <TeamOutlined style={{color: ACCENT}}/>
                        <span>Membres de « {selectedSquad?.Lib} »</span>
                    </Space>
                }
            >
                {membersLoading ? (
                    <div style={{textAlign: 'center', padding: 24}}>
                        <Spin/>
                    </div>
                ) : (
                    <List
                        locale={{emptyText: <Empty description="Aucun membre."/>}}
                        dataSource={members}
                        renderItem={(m: IUsrSqdCmpEdt) => (
                            <List.Item>
                                <List.Item.Meta
                                    avatar={<UserOutlined style={{fontSize: 18, color: ACCENT}}/>}
                                    title={memberDisplayName(m.Usr)}
                                    description={m.CmpEdt?.Lib ? <Tag>{m.CmpEdt.Lib}</Tag> : null}
                                />
                            </List.Item>
                        )}
                    />
                )}
            </Modal>
        </div>
    );
};

export default Squads;
